import { useCallback, useEffect, useState } from 'react';

interface ThemeConfig {
  mode: 'light' | 'dark' | 'system';
  primaryColor: string;
  secondaryColor: string;
  fontFamily: string;
  borderRadius: number;
  customVariables?: Record<string, string>;
}

const THEME_STORAGE_KEY = 'ai-tutor-theme';

const defaultTheme: ThemeConfig = {
  // 'system' so a first-time visitor on a dark OS gets dark immediately.
  // Previously 'light', which meant `prefers-color-scheme` was only consulted
  // once a user had explicitly chosen 'system' - so dark-OS users got a white
  // flash and a light app on first load. Anyone with a saved preference is
  // unaffected: localStorage still wins via readStoredTheme().
  mode: 'system',
  primaryColor: '#3b82f6',
  secondaryColor: '#64748b',
  fontFamily: "'Inter Variable', 'Inter', system-ui, sans-serif",
  borderRadius: 8,
  customVariables: {},
};

// Read the persisted theme synchronously so the very first render already
// has the correct mode. Doing this in a useEffect instead caused a race:
// the save effect below would write the initial `light` default back to
// localStorage before the load's setState flushed, and under React
// StrictMode's mount/remount that permanently clobbered a saved dark
// preference. Lazy init sidesteps the race entirely and also removes a
// flash of the wrong theme on load.
const readStoredTheme = (): ThemeConfig => {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored) {
      return { ...defaultTheme, ...JSON.parse(stored) };
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to load theme from localStorage:', error);
  }
  return defaultTheme;
};

export const useTheme = () => {
  const [theme, setThemeState] = useState<ThemeConfig>(readStoredTheme);

  // Save theme to localStorage when it changes
  useEffect(() => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(theme));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Failed to save theme to localStorage:', error);
    }
  }, [theme]);

  // Apply theme to document root
  useEffect(() => {
    const root = document.documentElement;

    // Apply CSS custom properties.
    //
    // Namespaced `--app-font-family`, not `--font-family`: Excalidraw's
    // stylesheet reads `var(--font-family)`, and setting the bare name on
    // documentElement inherited our font into the doubt canvas, overriding the
    // `--ui-font` (Assistant) it ships with. Keep app variables namespaced so
    // they can't collide with a vendor stylesheet again.
    Object.entries({
      '--primary-color': theme.primaryColor,
      '--secondary-color': theme.secondaryColor,
      '--app-font-family': theme.fontFamily,
      '--border-radius': `${theme.borderRadius}px`,
      ...theme.customVariables,
    }).forEach(([property, value]) => {
      root.style.setProperty(property, value);
    });

    // Apply theme mode class
    const applyMode = () => {
      root.classList.remove('light', 'dark');
      let isDark: boolean;
      if (theme.mode !== 'system') {
        isDark = theme.mode === 'dark';
      } else {
        // For system theme, follow the OS preference.
        isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      }
      root.classList.add(isDark ? 'dark' : 'light');

      // Keep the browser chrome bar in step. Only the pre-paint bootstrap in
      // index.html used to set this, so toggling the theme in-app left it
      // stale on the previous theme's colour. These must track `--ground` in
      // globals.css.
      const meta = document.querySelector('meta[name="theme-color"]');
      if (meta) meta.setAttribute('content', isDark ? '#111113' : '#e0e8ff');
    };

    applyMode();

    // In 'system' mode, keep following the OS as it changes at runtime
    // (e.g. the user flips their macOS appearance while the app is open).
    if (theme.mode === 'system') {
      const mql = window.matchMedia('(prefers-color-scheme: dark)');
      mql.addEventListener('change', applyMode);
      return () => mql.removeEventListener('change', applyMode);
    }
  }, [theme]);

  const setTheme = useCallback((updates: Partial<ThemeConfig>) => {
    setThemeState(prev => ({ ...prev, ...updates }));
  }, []);

  const toggleMode = useCallback(() => {
    // Resolve 'system' to what is on screen first. Without this, toggling from
    // 'system' always set 'light', so a user whose OS is light saw the button
    // do nothing. Now the toggle always flips the visible theme.
    const effective =
      theme.mode === 'system'
        ? window.matchMedia('(prefers-color-scheme: dark)').matches
          ? 'dark'
          : 'light'
        : theme.mode;
    setTheme({ mode: effective === 'dark' ? 'light' : 'dark' });
  }, [theme.mode, setTheme]);

  const resetTheme = useCallback(() => {
    setThemeState(defaultTheme);
  }, []);

  return {
    theme,
    setTheme,
    toggleMode,
    resetTheme,
  };
};
